import * as React from 'react';
import { useState } from 'react';
import { connect } from 'react-redux';
import {
	Typography,
	TextField,
	Button,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Table,
	Grid,
	Checkbox,
	IconButton,
} from '@mui/material';
import { RiDeleteBinLine, RiPencilLine } from 'react-icons/ri';
import { Theme } from '@mui/material/styles';

import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';

import { VatTable, ProductApi } from 'common/types';
import { ReduxState } from 'services/types';
import classnames from 'classnames';
import _ from 'lodash';

import ContainerBox from 'components/ContainerBox';
import { useTranslation } from 'services/localization/useTranslation';
import { useTheme } from '@mui/material';

interface StateToProps {
	stockProducts: ProductApi[];
}

interface OwnProps {
	vatTable: VatTable;
	setVatTable: (vatTable: VatTable) => void;
}

interface VatRow {
	value: number | '';
	description: string;
}

type Props = StateToProps & OwnProps & WithStyles<typeof styles>;

const TaxSettings = (props: Props) => {
	const { classes } = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const [editedVatRow, setEditedVatRow] = useState<VatRow | null>(null);
	const [editRowIndex, setEditRowIndex] = useState(-1);
	const [addingNewRow, setAddingNewRow] = useState(false);
	const [editingVatValue, setEditingVatValue] = useState(false);
	const vatTable = props.vatTable;
	const [selectedRow, setSelectedRow] = useState(
		vatTable.vatValues.findIndex((vat) => vat.value === vatTable.defaultVat),
	);
	const [error, setError] = useState('');

	const emptyRow: VatRow = {
		value: '',
		description: '',
	};

	const handleNewVatValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		let newVat: '' | number = parseFloat(event.target.value);
		if (!newVat && newVat !== 0) {
			newVat = '';
		}
		if (newVat >= 0 && newVat < 100) {
			setEditedVatRow({
				description: (editedVatRow && editedVatRow.description) || '',
				value: newVat,
			});
			setEditingVatValue(true);
		}
		setError('');
	};

	const handleNewVatDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
		setEditedVatRow({
			value: (editedVatRow && editedVatRow.value) || 0,
			description: event.target.value,
		});
		setEditingVatValue(false);
	};

	const removeVatRow = (vat: number | '') => {
		const oldVatTable = vatTable;
		const oldValue = vat !== '' ? vat : -1;
		if (!oldVatTable.vatValues.map((v) => v.value).includes(oldValue)) {
			return;
		}
		const newVatValues = oldVatTable.vatValues
			.filter((vatEntry) => vatEntry.value !== oldValue)
			.sort((a, b) => a.value - b.value);
		if (!newVatValues.length) {
			return;
		}
		const deletingCurrentDefault = oldVatTable.defaultVat === oldValue;
		const newDefault = deletingCurrentDefault
			? newVatValues[newVatValues.length - 1].value
			: oldVatTable.defaultVat;
		const updatedVatTable: VatTable = {
			vatValues: newVatValues,
			defaultVat: newDefault,
		};

		setSelectedRow(newVatValues.findIndex((v) => v.value === updatedVatTable.defaultVat));
		props.setVatTable(updatedVatTable);
	};

	const addVatRow = () => {
		setAddingNewRow(true);
		setEditedVatRow(emptyRow);
		setEditRowIndex(vatTable.vatValues.length);
		setEditingVatValue(true);
		setError('');
	};

	const resetVatRowEdit = () => {
		setEditedVatRow(null);
		setEditRowIndex(-1);
		setAddingNewRow(false);
		setEditingVatValue(false);
		setError('');
	};

	const saveEditedVatRow = (index: number) => {
		const oldVatTable = vatTable;
		const thisRowIsSelected = selectedRow === index;
		const newValueExistsInVatTable = oldVatTable.vatValues
			.filter((v, i) => i !== index)
			.map((v) => v.value)
			.includes(editedVatRow!.value || -1);
		const rowToBeAdded: { value: number; description: string } = {
			value: (editedVatRow && editedVatRow.value) || 0,
			description: (editedVatRow && editedVatRow.description) || '',
		};
		if (_.isEqual(rowToBeAdded, oldVatTable.vatValues[index])) {
			resetVatRowEdit();
			return;
		}
		if (!rowToBeAdded || (newValueExistsInVatTable && editingVatValue)) {
			setError(t('shop.vatAlreadyExists', 'VAT already exists'));
			return;
		}
		let updatedVatTable = Object.assign({}, oldVatTable);
		if (!addingNewRow) {
			const newVatValues = [...updatedVatTable.vatValues];
			newVatValues[index] = rowToBeAdded;
			const defaultVat = thisRowIsSelected
				? rowToBeAdded.value
				: oldVatTable.vatValues[selectedRow].value;
			updatedVatTable = {
				vatValues: newVatValues.sort((a, b) => a.value - b.value),
				defaultVat,
			};
			setSelectedRow(updatedVatTable.vatValues.findIndex((v) => v.value === defaultVat));
		} else {
			const vatValues = [...oldVatTable.vatValues, rowToBeAdded].sort((a, b) => a.value - b.value);
			updatedVatTable = {
				...oldVatTable,
				vatValues,
			};
			setSelectedRow(vatValues.findIndex((v) => v.value === updatedVatTable.defaultVat));
		}

		props.setVatTable(updatedVatTable);
		resetVatRowEdit();
	};

	const handleNewDefaultVat = (index: number) => {
		setSelectedRow(index);
		const updatedVatTable = {
			...vatTable,
			defaultVat: vatTable.vatValues[index].value,
		};

		props.setVatTable(updatedVatTable);
	};

	const renderVatRow = (
		{ value, description }: { value: number | ''; description: string },
		index: number,
	) => {
		const isEditing = editRowIndex === index;
		const vatValue = isEditing
			? editedVatRow && (editedVatRow.value === 0 ? '0' : editedVatRow.value)
			: value;
		const styles =
			!addingNewRow && index === vatTable.vatValues.length ? classnames(classes.hidden) : '';
		return (
			<TableRow key={index} className={styles}>
				<TableCell padding="checkbox">
					{index < vatTable.vatValues.length && (
						<Checkbox
							checked={selectedRow === index}
							onClick={() => handleNewDefaultVat(index)}
							color="primary"
						/>
					)}
				</TableCell>
				<TableCell>
					<TextField
						name="vatValue"
						variant="standard"
						value={vatValue}
						onChange={handleNewVatValue}
						className={classnames(classes.vatValueField, classes.vatTableField)}
						margin="none"
						type="number"
						InputProps={{
							disableUnderline: !isEditing,
							readOnly: !isEditing,
							classes: {
								input: classes.disableArrows,
							},
						}}
					/>
				</TableCell>
				<TableCell>
					<TextField
						name="vatDescription"
						variant="standard"
						value={isEditing ? editedVatRow && editedVatRow.description : description}
						className={classes.vatTableField}
						onChange={handleNewVatDescription}
						margin="none"
						type="text"
						InputProps={{
							disableUnderline: !isEditing,
							readOnly: !isEditing,
						}}
					/>
				</TableCell>
				<TableCell>
					<Grid container spacing={1} justifyContent="flex-end">
						{isEditing ? (
							<React.Fragment>
								<Grid item>
									<Button variant="outlined" color="primary" onClick={resetVatRowEdit}>
										{t('shop.cancel')}
									</Button>
								</Grid>
								<Grid item>
									<Button
										variant="contained"
										color="primary"
										onClick={() => {
											saveEditedVatRow(index);
										}}
									>
										{t('common:actions.save')}
									</Button>
								</Grid>
							</React.Fragment>
						) : (
							<React.Fragment>
								<Grid item>
									<IconButton
										onClick={() => {
											resetVatRowEdit();
											setEditedVatRow({ value, description });
											setEditRowIndex(index);
										}}
										size="large"
									>
										<RiPencilLine color={theme.palette.primary.main} />
									</IconButton>
								</Grid>
								<Grid item>
									<IconButton
										disabled={vatTable.vatValues.length < 2}
										onClick={() => {
											if (value !== '') {
												removeVatRow(value);
											}
										}}
										size="large"
									>
										<RiDeleteBinLine
											color={
												vatTable.vatValues.length < 2
													? theme.palette.action.disabled
													: theme.palette.primary.main
											}
										/>
									</IconButton>
								</Grid>
							</React.Fragment>
						)}
					</Grid>
				</TableCell>
			</TableRow>
		);
	};

	return (
		<ContainerBox>
			<Typography variant="h5" gutterBottom className={classes.bottomSpacing}>
				{t('shop.taxSettings', 'Tax Settings')}
			</Typography>
			<Grid container className={classes.bottomSpacing}>
				<Grid item xs={12}>
					<Table className={classes.table}>
						<TableHead>
							<TableRow>
								<TableCell style={{ width: '10%' }}>
									{t('shop.defaultTax', 'Default Tax')}
								</TableCell>
								<TableCell style={{ width: '25%' }}>
									{t('shop.taxTableTitle', 'Tax percentage')}
								</TableCell>
								<TableCell style={{ width: '40%' }}>
									{t('vatTableDescriptionTitle', 'Description')}
								</TableCell>
								<TableCell style={{ width: '25%' }} />
							</TableRow>
						</TableHead>
						<TableBody>
							{[...vatTable.vatValues].sort((a, b) => a.value - b.value).map(renderVatRow)}
							{renderVatRow(editedVatRow || emptyRow, vatTable.vatValues.length)}
						</TableBody>
					</Table>
				</Grid>
			</Grid>
			<Grid container>
				<Grid item>
					<Typography variant="caption" color="error">
						{error}
					</Typography>
				</Grid>
			</Grid>
			<Grid container>
				<Grid item>
					<Button variant="outlined" color="primary" onClick={() => addVatRow()}>
						{t('shop.addTax', 'Add Tax rate')}
					</Button>
				</Grid>
			</Grid>
		</ContainerBox>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		section: {
			padding: '32px 0px',
		},
		bottomSpacing: {
			marginBottom: theme.spacing(2),
		},
		fab: {
			margin: theme.spacing(1),
		},
		table: {
			minWidth: 350,
		},
		vatValueField: {
			maxWidth: 80,
		},
		vatTableField: {
			fontSize: '1.2rem',
			fontFamily: theme.typography.fontFamily,
			fontWeight: 400,
			lineHeight: 1.66,
			width: '100%',
		},
		disableArrows: {
			WebkitAppearance: 'textfield',
			margin: 0,
		},
		hidden: {
			display: 'none',
		},
		dialog: {
			margin: 0,
			padding: theme.spacing(2),
		},
	});

const mapStateToProps = ({ stock }: ReduxState): StateToProps => {
	const { stockProducts } = stock;
	return {
		stockProducts: stockProducts.data!,
	};
};

export default withStyles(styles)(connect(mapStateToProps, {})(TaxSettings));
